import some from "lodash/some";

import { useOperator } from "globals/hooks";
import { PricingLayout, Request, RoutePricing } from "types";
import { currencyConverter } from "utils/currency";
import { derivePricingGroups } from "components/price-summary/utils/pricingGroups";

export function calculateAdditionalSubtotalPercentageAmt(
  pricing: RoutePricing,
  additionalGratuityAmt: number,
  pricingLayout: PricingLayout | null
): number {
  const { subtotalItems, calculatedOnSubtotalItems } =
    derivePricingGroups(pricingLayout);

  if (
    !additionalGratuityAmt ||
    calculatedOnSubtotalItems.includes("driverGratuity")
  ) {
    return 0;
  }

  // base rate in cents
  let subtotalCents = currencyConverter.toCents(pricing.baseRateAmt || 0);

  // adding subtotal items to base rate
  for (const key of subtotalItems) {
    const amount = pricing[`${key}Amt` as keyof RoutePricing];

    if (typeof amount !== "number") continue;

    subtotalCents +=
      (key === "promoDiscount" ? -1 : 1) * currencyConverter.toCents(amount);
  }

  // base rate + subtotal items + added gratuity
  const newSubtotalCents =
    subtotalCents + currencyConverter.toCents(additionalGratuityAmt);

  // calculate additional cents to be added to the new subtotal with its on subtotal items
  let additionalCents = 0;
  for (const key of calculatedOnSubtotalItems) {
    const rate = pricing[`${key}Percent` as keyof RoutePricing];

    if (typeof rate !== "number") continue;

    const previousAmount = pricing[`${key}Amt` as keyof RoutePricing];

    const previousCents =
      typeof previousAmount === "number"
        ? currencyConverter.toCents(previousAmount)
        : 0;

    const newCents = currencyConverter.toCents(
      currencyConverter.toDollars(newSubtotalCents) * rate
    );

    additionalCents +=
      (key === "promoDiscount" ? -1 : 1) * (newCents - previousCents);
  }

  return currencyConverter.toDollars(additionalCents);
}

type UseAdditionalDriverGratuityByRouteProps = {
  request?: Request;
  userSelectedDriverGratuityPercent: number;
  shouldAllowDriverGratuity?: boolean;
};

function useAdditionalDriverGratuityByRoute(
  props: UseAdditionalDriverGratuityByRouteProps
) {
  const {
    request,
    userSelectedDriverGratuityPercent,
    shouldAllowDriverGratuity = true,
  } = props;

  // hooks
  const { operator } = useOperator();

  if (!request) return {};

  const isPaid = request.amountDue <= 0;
  const driverGratuityExist = some(
    request.trips,
    (trip) => trip.routes[0].pricing.driverGratuityAmt
  );

  const canAddDriverGratuityToRoutes =
    shouldAllowDriverGratuity &&
    !driverGratuityExist &&
    operator.gratuityWhenBooking &&
    !isPaid;

  let additionalDriverGratuityByRoute;
  let additionalDriverGratuityAmt = 0;
  let additionalSubtotalPercentageAmt = 0;

  if (canAddDriverGratuityToRoutes && userSelectedDriverGratuityPercent) {
    additionalDriverGratuityByRoute = request.trips.map((trip) => {
      const { baseRateAmt } = trip.routes[0].pricing;

      const driverGratuityAmt =
        (userSelectedDriverGratuityPercent / 100) * baseRateAmt;
      const roundedDriverGratuityAmt = currencyConverter.toDollars(
        currencyConverter.toCents(driverGratuityAmt)
      );

      additionalDriverGratuityAmt += roundedDriverGratuityAmt;
      additionalSubtotalPercentageAmt +=
        calculateAdditionalSubtotalPercentageAmt(
          trip.routes[0].pricing,
          roundedDriverGratuityAmt,
          operator.pricingLayout
        );

      return {
        routeId: trip.routes[0].id,
        driverGratuityAmt: roundedDriverGratuityAmt,
      };
    });
  }

  return {
    displayGratuityBlock: canAddDriverGratuityToRoutes,
    additionalDriverGratuityAmt: Number(additionalDriverGratuityAmt.toFixed(2)),
    additionalSubtotalPercentageAmt: Number(
      additionalSubtotalPercentageAmt.toFixed(2)
    ),
    additionalDriverGratuityByRoute,
  };
}

export default useAdditionalDriverGratuityByRoute;
